import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PasswordRuleResult,
  PasswordValidationConfig,
  PasswordValidationRule
} from '../ui/components/ChangePasswordInputs/ChangePasswordInputs.types'

export const useChangePasswordValidations = (validationConfig: PasswordValidationConfig) => {
  const { t } = useTranslation()

  const { MIN_LENGTH, REQUIRE_UPPERCASE, REQUIRE_LOWERCASE, REQUIRE_NUMBERS, REQUIRE_SPECIAL_CHARS } = validationConfig

  const validateIndividualRule = useCallback(
    (password: string, rule: PasswordValidationRule): boolean => {
      if (!password) return false

      switch (rule) {
        case PasswordValidationRule.MIN_LENGTH:
          return password.length >= MIN_LENGTH
        case PasswordValidationRule.REQUIRE_UPPERCASE:
          return /[A-Z]/.test(password)
        case PasswordValidationRule.REQUIRE_LOWERCASE:
          return /[a-z]/.test(password)
        case PasswordValidationRule.REQUIRE_NUMBERS:
          return /\d/.test(password)
        case PasswordValidationRule.REQUIRE_SPECIAL_CHARS:
          return /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)
        default:
          return false
      }
    },
    [MIN_LENGTH]
  )

  const getAllPasswordRules = useCallback(
    (password: string): PasswordRuleResult[] => {
      const rules: PasswordRuleResult[] = []

      rules.push({
        rule: PasswordValidationRule.MIN_LENGTH,
        label: t('password_strength.at_least_n_characters', { n: MIN_LENGTH }),
        isValid: validateIndividualRule(password, PasswordValidationRule.MIN_LENGTH),
        isRequired: true
      })

      if (REQUIRE_UPPERCASE) {
        rules.push({
          rule: PasswordValidationRule.REQUIRE_UPPERCASE,
          label: t('password_strength.at_least_1_uppercase_letter'),
          isValid: validateIndividualRule(password, PasswordValidationRule.REQUIRE_UPPERCASE),
          isRequired: REQUIRE_UPPERCASE
        })
      }

      if (REQUIRE_LOWERCASE) {
        rules.push({
          rule: PasswordValidationRule.REQUIRE_LOWERCASE,
          label: t('password_strength.at_least_1_lowercase_letter'),
          isValid: validateIndividualRule(password, PasswordValidationRule.REQUIRE_LOWERCASE),
          isRequired: REQUIRE_LOWERCASE
        })
      }

      if (REQUIRE_NUMBERS) {
        rules.push({
          rule: PasswordValidationRule.REQUIRE_NUMBERS,
          label: t('password_strength.at_least_1_number'),
          isValid: validateIndividualRule(password, PasswordValidationRule.REQUIRE_NUMBERS),
          isRequired: REQUIRE_NUMBERS
        })
      }

      if (REQUIRE_SPECIAL_CHARS) {
        rules.push({
          rule: PasswordValidationRule.REQUIRE_SPECIAL_CHARS,
          label: t('password_strength.at_least_1_special_character'),
          isValid: validateIndividualRule(password, PasswordValidationRule.REQUIRE_SPECIAL_CHARS),
          isRequired: REQUIRE_SPECIAL_CHARS
        })
      }

      return rules
    },
    [
      t,
      MIN_LENGTH,
      REQUIRE_UPPERCASE,
      REQUIRE_LOWERCASE,
      REQUIRE_NUMBERS,
      REQUIRE_SPECIAL_CHARS,
      validateIndividualRule
    ]
  )

  const validateAllRules = useCallback(
    (password: string): string | null => {
      if (!password) {
        return t('password_strength.at_least_n_characters', { n: MIN_LENGTH })
      }

      if (password.length < MIN_LENGTH) {
        return t('password_strength.at_least_n_characters', { n: MIN_LENGTH })
      }

      if (REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
        return t('password_strength.at_least_1_uppercase_letter')
      }

      if (REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
        return t('password_strength.at_least_1_lowercase_letter')
      }

      if (REQUIRE_NUMBERS && !/\d/.test(password)) {
        return t('password_strength.at_least_1_number')
      }

      if (REQUIRE_SPECIAL_CHARS && !/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
        return t('password_strength.at_least_1_special_character')
      }

      return null
    },
    [t, MIN_LENGTH, REQUIRE_UPPERCASE, REQUIRE_LOWERCASE, REQUIRE_NUMBERS, REQUIRE_SPECIAL_CHARS]
  )

  return {
    validateAllRules,
    getAllPasswordRules,
    validateIndividualRule
  }
}
